import {canonicalCitations, citationCommentRef} from '../../../utils/citationNavigation';
import React, {useCallback, useEffect, useRef, useState, useMemo} from 'react';
import {Alert, Button, Drawer, message, Modal, Tooltip, Typography} from 'antd';
import {useDispatch, useSelector} from "react-redux";
import {useParams, useLocation} from "react-router-dom";

// Composants refactorisés
import RightPanel from "./rightPanel";
import CriterionFormModal from "../../critères/CriterionFormModal";

// Hooks personnalisés
import {useUIState} from "./useUiState";
import {useMetadataManagement} from "./userMetadataManagement";
import {useVersionStatusPolling} from "./userVersionStatusPolling";
import {useCanCreateCriteria} from "../../../utils/roles";
import {fetchStageData, launchAnalysisStreamRequest, selectTemplate, replaceCriteriaSelection, addCriterionToList, resetAnalysisReasoning} from "../../../Actions/stage.action";
import {loadVersionContent} from "../../../Actions/documents.action";
import {RequestCreateAnalyseWithUpload} from "../../../Actions/review.action";
import {RequestCreateCriterion} from "../../../Actions/critere.action";
import {
    hasCurrentReferenceReviewRunForScope,
    hydrateLatestReferenceReviewSnapshot,
} from "../../../Actions/referenceReview.action";
import Criteria from "../criteria/criteria";
import useVersionComparison from '../criteria/useVersionComparison';
import { applyVersionComparisonSelection, versionComparisonError } from '../../../utils/versionComparison';
import { launchVersionComparisonRequest } from '../../../Actions/versionComparison.action';
import AnalyseMetadataPanel from "../create/AnalyseMetadataPanel";
import {
    isMetadataComplete,
    normalizeMetadataDraft,
    isMetadataListPresent,
} from "../../../utils/metadataNormalizer";
import ReviewHeader from "./reviewHeader";
import DocumentPanel from "./documentPanel";
import {useTranslation} from "react-i18next";
import {CloseOutlined, MenuFoldOutlined, MenuUnfoldOutlined, PushpinOutlined, SettingOutlined, CheckSquareOutlined, PlusOutlined, CommentOutlined, FileTextOutlined, ThunderboltOutlined, ExclamationCircleOutlined} from "@ant-design/icons";
import { startCinematicUpload, resetCinematicUpload } from "../../../Reducers/uiReducer";
import {
    findInvalidReferenceSelection,
    getComparisonReviewMode,
    getSelectionReferenceIds,
    getValidUniqueReferenceIds,
    isReferenceComparisonCriterion,
    isReferenceReviewCriterion,
    REFERENCE_SELECTION_ERROR_CODES,
    REFERENCE_SELECTION_ERROR_REASONS,
} from "../../../utils/comparisonRisk";
import {
    getStandardCriteriaCountForSelection,
    normalizeCriteriaSelectionForAvailableCriteria,
} from "../../../utils/criteriaSelection";
import { findGlobalAnalysisCriterion } from "../../../utils/globalAnalysisCriterion";
import { hydrateFastGlobalCitationAnchor } from "../../../utils/fastGlobalStream";
import { FeedbackProvider } from "../feedback/FeedbackContext";
import FeedbackDock from "../feedback/FeedbackDock";
import FeedbackDrawer from "../feedback/FeedbackDrawer";
import {
    buildPartialAnalysisRetryContext,
    clearPartialAnalysisDecision,
    closeCommercialGateModal,
    fetchCommercialUsage,
    normalizeCommercialDecision,
    openCommercialGateModal,
    runCommercialPreflightLaunchGate,
    setPartialAnalysisDecision,
} from "../../../Actions/commercialControl.action";
import CommercialGateModal from "../commercial/CommercialGateModal";
import {
    computeReviewMobileAutoOpenActions,
    createReviewMobileAutoOpenState,
    getReviewMobileAutoOpenAnalysisKey,
    getReviewMobileCommentTarget,
    markReviewMobileAutoOpenDismissed,
    markReviewMobileLaunchOpenedComments,
} from "./reviewMobileAutoOpen";
import { getNextCommentIdForRef } from "./commentSelection";
import {getDisplayableReferenceReviewSnapshot} from "../reference-review/referenceReviewProjection";
import {getAnalysisPov, hasCompletePov} from "../../../utils/pov";
import './ReviewPage.css';

const { Text, Title } = Typography;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (value) => UUID_RE.test(String(value || ""));
const REVIEW_MOBILE_PANEL_BREAKPOINT = 1024;
const REVIEW_MOBILE_SHEET_ENABLED = true;
const getIsReviewPanelMobile = () => (
    typeof window !== "undefined" && window.innerWidth < REVIEW_MOBILE_PANEL_BREAKPOINT
);

const buildLaunchPreflightPayload = ({
    stage,
    documentVersion,
    documentVersionId,
    criteriaToSend,
    criteria,
}) => ({
    action: "launch_analysis",
    analysis_id: stage?.id,
    document_version_id: documentVersionId,
    file_format: documentVersion?.content?.file_extension || null,
    criteria_count: getStandardCriteriaCountForSelection(criteriaToSend, criteria),
    page_count: documentVersion?.content?.page_count ?? null,
    page_mapping_reliable: documentVersion?.content?.page_mapping_reliable ?? null,
});

const StageReview = () => {
    const {t} = useTranslation('review');
    const {stage} = useSelector((state) => state);
    const documentVersion = useSelector((state) => state.documentVersion);
    const commercialControl = useSelector((state) => state.commercialControl);
    const currentUser = useSelector((state) => (
        state.users?.user?.id ? state.users.user : state.users?.currentUser || {}
    ));
    const currentRole = String(currentUser?.role || "").toUpperCase();
    const canManageBilling = ["SUPER_ADMIN", "ADMIN_CLIENT"].includes(currentRole);
    const billingReturnPath = currentRole === "SUPER_ADMIN"
        ? "/admin/commercial?tab=subscriptions"
        : "/workspace";
    const ia_streaming = useSelector((state) => state.documentVersion.ia_streaming);
    const {isLoading} = useSelector(state => state.documentVersion);
    const cinematicActive = useSelector(state => !!state.ui?.cinematicUpload?.active);
    const {id_review} = useParams();
    const location = useLocation();
    const isCreate = !id_review || id_review === "create";
    const dispatch = useDispatch();
    const {isGlobalLoading} = useSelector(state => state.stage);
    const [forcedActiveTab, setForcedActiveTab] = useState({ key: null, ts: 0 });
    const [criteriaRenderKey, setCriteriaRenderKey] = useState(0);
    
    // État pour ouvrir le modal de critères depuis la collapsed sidebar
    const [openCriteriaModalFlag, setOpenCriteriaModalFlag] = useState(0);
    // État pour le modal de création de critère au niveau page
    const [criterionModalOpen, setCriterionModalOpen] = useState(false);
    const [analysisMetadataModalOpen, setAnalysisMetadataModalOpen] = useState(false);
    const [isSavingAnalysisMetadata, setIsSavingAnalysisMetadata] = useState(false);
    const [isConfirmingAnalysisMetadataSkip, setIsConfirmingAnalysisMetadataSkip] = useState(false);
    const analysisMetadataPanelRef = useRef(null);
    const [pendingPartialLaunch, setPendingPartialLaunch] = useState(null);
    const [isReviewPanelMobile, setIsReviewPanelMobile] = useState(getIsReviewPanelMobile);
    const [activeMobilePanel, setActiveMobilePanel] = useState("document");
    const [comparisonLaunching, setComparisonLaunching] = useState(false);
    const [comparisonLaunchError, setComparisonLaunchError] = useState('');
    const comparisonLaunchPending = useRef(false);
    const comparisonCleanup = useRef(null);
    const comparison = useVersionComparison({
        documentId: stage?.principal_document_id,
        viewedVersionId: documentVersion?.currentVersion || documentVersion?.content?.id,
        enabled: (stage?.selectedCriteria || []).some(selection => (stage?.criteriaListe || []).some(
            criterion => criterion.id === selection.id && isReferenceComparisonCriterion(criterion)
        )),
    });
    useEffect(() => () => comparisonCleanup.current?.(), []);

    const activeAnalysisPov = useMemo(() => getAnalysisPov({
        party_role: stage?.main_document?.party_role,
        studied_party_name: stage?.main_document?.studied_party_name,
    }), [stage?.main_document?.party_role, stage?.main_document?.studied_party_name]);
    const hasCompleteAnalysisPov = hasCompletePov(activeAnalysisPov);

    const referenceReviewSnapshotVersionId = [
        documentVersion?.currentVersion,
        documentVersion?.content?.id,
        stage?.versions?.[0]?.id,
    ].find(isUuid);
    const rawReferenceReviewState = useMemo(
        () => documentVersion?.reference_review || {},
        [documentVersion?.reference_review],
    );
    const [mobileSheetState, setMobileSheetState] = useState("collapsed");
    const [activeMobileSheetPanel, setActiveMobileSheetPanel] = useState("comments");
    const [mobileSheetHeight, setMobileSheetHeight] = useState(null);
    const [isMobileSheetDragging, setIsMobileSheetDragging] = useState(false);
    const mobileSheetDragRef = useRef({ active: false });
    const mobileSheetClickBlockRef = useRef(false);
    const mobileAutoOpenRef = useRef(createReviewMobileAutoOpenState());
    
    // Hook de droits pour création de critères
    const canCreateCriteria = useCanCreateCriteria();
    
    // Sections existantes pour le formulaire de critère
    const existingSections = useMemo(() => {
        return [...new Set((stage?.criteriaListe || []).map(c => c.section).filter(Boolean))];
    }, [stage?.criteriaListe]);

    // Hooks personnalisés pour la gestion de l'état
    const {
        isCriteriaDrawerOpen,
        isMobile,
        isCriteriaSidebarOpen,
        isCommentsSidebarOpen,
        rightPanelMode,
        showAllComments,
        hoveredRef,
        selectedRef,
        setRightPanelMode,
        setShowAllComments,
        setHoveredRef,
        setSelectedRef,
        // toggleCriteriaSidebar, // unused
        openCriteriaDrawer,
        closeCriteriaDrawer,
        setIsCriteriaSidebarOpen,
        setIsCommentsSidebarOpen
    } = useUIState(isCreate);
    const [povFocusRequest, setPovFocusRequest] = useState(0);
    const {
        metadataDraft,
        metadataInitialValues,
        setMetadataDraft,
        saveMetadata,
        changePov,
        retryPovResults,
        guessMetadata,
        isGuessingMetadata,
        isPovTransitioning,
        povTransitionPartyName,
        povResultsError,
    } = useMetadataManagement(
        isCreate,
        id_review,
        stage,
        location,
        setRightPanelMode,
        documentVersion?.currentVersion
    );

    // During a POV transition the previous snapshot stays in Redux for rollback,
    // but it must never remain projectable in the UI.
    const referenceReviewState = useMemo(() => (
        hasCompleteAnalysisPov && !isPovTransitioning
            ? rawReferenceReviewState
            : {}
    ), [hasCompleteAnalysisPov, isPovTransitioning, rawReferenceReviewState]);
    const referenceReviewHasCurrentRun = hasCurrentReferenceReviewRunForScope(
        referenceReviewState,
        id_review,
        referenceReviewSnapshotVersionId,
    );
    const referenceReviewContextMatches = (
        String(referenceReviewState?.context?.analysis_id || '') === String(id_review || '')
        && String(referenceReviewState?.context?.document_version_id || '')
            === String(referenceReviewSnapshotVersionId || '')
    );
    const referenceReviewHydrationSettled = referenceReviewContextMatches
        && ['loading', 'loaded', 'absent', 'error', 'incompatible'].includes(
            referenceReviewState?.hydration_status,
        );

    useEffect(() => {
        if (
            isCreate
            || ia_streaming
            || isPovTransitioning
            || !hasCompleteAnalysisPov
            || referenceReviewHasCurrentRun
            || referenceReviewHydrationSettled
            || !isUuid(id_review)
            || !referenceReviewSnapshotVersionId
        ) return undefined;
        dispatch(hydrateLatestReferenceReviewSnapshot(
            id_review,
            referenceReviewSnapshotVersionId,
            activeAnalysisPov,
        ));
        return undefined;
    }, [
        activeAnalysisPov,
        dispatch,
        hasCompleteAnalysisPov,
        ia_streaming,
        id_review,
        isCreate,
        isPovTransitioning,
        referenceReviewHasCurrentRun,
        referenceReviewHydrationSettled,
        referenceReviewSnapshotVersionId,
    ]);
    const [selectedCommentId, setSelectedCommentId] = useState(null);
    const [commentFocusRequest, setCommentFocusRequest] = useState(null);
    const citationNavigationSequence = useRef(0);
    const [visibleCommentIdsForHighlights, setVisibleCommentIdsForHighlights] = useState(null);
    const [visibleCardTargetsForSelection, setVisibleCardTargetsForSelection] = useState(null);
    const [activeDocumentSource, setActiveDocumentSource] = useState('contract');
    const [referenceNavigationRequest, setReferenceNavigationRequest] = useState(null);
    const [comparisonVersionSource, setComparisonVersionSource] = useState(null);
    const [comparisonVersionNavigationRequest, setComparisonVersionNavigationRequest] = useState(null);
    const [comparisonContractNavigationRequest, setComparisonContractNavigationRequest] = useState(null);
    const [traceEvidenceNavigation, setTraceEvidenceNavigation] = useState(null);
    const previousReferenceSourceIdentityRef = useRef(null);

    const referenceViewerSource = useMemo(() => {
        const {snapshot} = getDisplayableReferenceReviewSnapshot(referenceReviewState);
        const metadata = snapshot?.snapshot_metadata || {};
        const context = referenceReviewState?.context || {};
        const referenceDocument = snapshot?.reference_document || metadata.reference_document || {};
        const analysisId = snapshot?.analysis_id || metadata.analysis_id || context.analysis_id || id_review;
        const documentVersionId = snapshot?.document_version_id
            || snapshot?.contract_document_version_id
            || metadata.document_version_id
            || metadata.contract_document_version_id
            || context.document_version_id
            || referenceReviewSnapshotVersionId;
        const reviewId = snapshot?.review_id || metadata.review_id || context.review_id;
        const snapshotId = snapshot?.snapshot_id || metadata.snapshot_id || context.snapshot_id || null;
        const referenceFileId = snapshot?.reference_document_id
            || snapshot?.reference_file_id
            || metadata.reference_document_id
            || metadata.reference_file_id
            || referenceDocument.file_id
            || context.reference_document_id;
        if (![analysisId, documentVersionId, reviewId, referenceFileId].every(isUuid)) return null;
        return {
            analyseId: analysisId,
            analysisId,
            documentVersionId,
            reviewId,
            snapshotId: isUuid(snapshotId) ? snapshotId : null,
            referenceFileId,
            title: snapshot?.reference_document_title
                || metadata.reference_document_title
                || referenceDocument.title
                || context.reference_document_title
                || t('referenceViewer.reference'),
        };
    }, [id_review, referenceReviewSnapshotVersionId, referenceReviewState, t]);

    const referenceViewerNavigationIdentity = referenceViewerSource
        ? [
            referenceViewerSource.analysisId,
            referenceViewerSource.documentVersionId,
            referenceViewerSource.reviewId,
            referenceViewerSource.referenceFileId,
        ].join(':')
        : null;

    useEffect(() => {
        if (previousReferenceSourceIdentityRef.current === referenceViewerNavigationIdentity) return;
        previousReferenceSourceIdentityRef.current = referenceViewerNavigationIdentity;
        setActiveDocumentSource('contract');
        setReferenceNavigationRequest(null);
    }, [referenceViewerNavigationIdentity]);

    const openCriteriaSidebarWithAnimation = useCallback(() => {
        setCriteriaRenderKey((k) => k + 1);
        setIsCriteriaSidebarOpen(true);
    }, [setIsCriteriaSidebarOpen]);

    // Ouvre le modal de critère quand le flag change (depuis la collapsed sidebar)
    useEffect(() => {
        if (openCriteriaModalFlag > 0) {
            setCriterionModalOpen(true);
        }
    }, [openCriteriaModalFlag]);

    // Données du document
    const currentVersion = documentVersion?.currentVersion;
    const versions = useSelector(state => state.stage?.versions);
    const currentVersionId = useSelector(state => state.documentVersion?.currentVersion);

    useEffect(() => {
        setTraceEvidenceNavigation(null);
    }, [currentVersionId]);

    const headerTitle = versions?.find(v => v.id === currentVersionId)?.title;
    const shouldUseReviewMobilePanels = isReviewPanelMobile && !isCreate;
    const shouldUseReviewMobileSheet = shouldUseReviewMobilePanels && REVIEW_MOBILE_SHEET_ENABLED;
    const shouldShowReviewMobilePanelNav = shouldUseReviewMobilePanels && !shouldUseReviewMobileSheet;
    const mobileCriteriaCount = stage?.criteriaListe?.length || 0;


    const content = documentVersion?.content;

    // Polling du status de version
    // États calculés
    // const hasComments = (documentVersion?.ia_comments?.length || 0) > 0; // unused
    const showSettings = rightPanelMode === "settings";

    // Normalisation exacte comme dans l'original
    const normalizeBbox = (bbox) => {
        if (!bbox) return null;
        const normalized = {
            left: bbox.left ?? bbox.Left,
            top: bbox.top ?? bbox.Top,
            width: bbox.width ?? bbox.Width,
            height: bbox.height ?? bbox.Height,
        };
        const isValid = ["left", "top", "width", "height"].every((key) => Number.isFinite(Number(normalized[key])))
            && Number(normalized.width) > 0
            && Number(normalized.height) > 0;
        return isValid ? normalized : null;
    };

    const normalizedComments = useMemo(() => {
        if (!hasCompleteAnalysisPov || isPovTransitioning) return [];
        return (documentVersion?.ia_comments || []).map(c => {
        const citationAnchor = hydrateFastGlobalCitationAnchor(c);
        const persistedBbox = normalizeBbox({
            left: c.bbox_left ?? c.bbox?.left,
            top: c.bbox_top ?? c.bbox?.top,
            width: c.bbox_width ?? c.bbox?.width,
            height: c.bbox_height ?? c.bbox?.height,
        }) || normalizeBbox(citationAnchor.bbox);
        const highlights = Array.isArray(c.highlights)
            ? c.highlights
                .map((h) => ({ ...h, bbox: normalizeBbox(h.bbox) }))
                .filter((h) => h.bbox)
            : [];
        return {
            ...c,
            ref: citationCommentRef(c) || (c.ref && c.ref !== 'global' ? c.ref : citationAnchor.ref),
            page: c.page || citationAnchor.page,
            file_id: c.file_id || citationAnchor.file_id,
            quote: c.quote || citationAnchor.quote,
            bbox: persistedBbox,
            highlights,
        };
        });
    }, [documentVersion?.ia_comments, hasCompleteAnalysisPov, isPovTransitioning]);
    const highlightedComments = useMemo(() => {
        if (!Array.isArray(visibleCommentIdsForHighlights)) {
            return normalizedComments;
        }
        const visibleIds = new Set(visibleCommentIdsForHighlights.map((id) => String(id)));
        return normalizedComments.filter((comment) => visibleIds.has(String(comment.id)));
    }, [normalizedComments, visibleCommentIdsForHighlights]);

    const handleVisibleCommentsChange = useCallback((visibleComments) => {
        if (!Array.isArray(visibleComments)) {
            setVisibleCommentIdsForHighlights(null);
            setVisibleCardTargetsForSelection(null);
            return;
        }
        const nextIds = visibleComments.map((comment) => comment.id);
        const nextTargets = visibleComments
            .filter((comment) => comment?.id != null && comment?.ref)
            .map((comment) => ({id: comment.id, ref: comment.ref}));
        setVisibleCardTargetsForSelection(nextTargets);
        setVisibleCommentIdsForHighlights((previousIds) => {
            if (
                Array.isArray(previousIds)
                && previousIds.length === nextIds.length
                && previousIds.every((id, index) => String(id) === String(nextIds[index]))
            ) {
                return previousIds;
            }
            return nextIds;
        });
    }, []);

    const isProcessing = content?.extraction_status === "processing";
    const isOcrAnalysisBlocked = content?.analysis_allowed === false && !!content?.ocr_user_message;
    const reviewMobileAutoOpenAnalysisKey = useMemo(() => getReviewMobileAutoOpenAnalysisKey({
        reviewId: id_review,
        currentVersionId,
        documentVersionId: documentVersion?.currentVersion,
        contentId: content?.id,
    }), [content?.id, currentVersionId, documentVersion?.currentVersion, id_review]);
    const isAnalyzing = ia_streaming;

    const hasList = (v, field = "metadata") => isMetadataListPresent(v, field);

    useEffect(() => {
        const handleResize = () => {
            setIsReviewPanelMobile(getIsReviewPanelMobile());
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (!shouldUseReviewMobilePanels || shouldUseReviewMobileSheet) return;

        if (activeMobilePanel === "document") {
            setForcedActiveTab({ key: "contract", ts: Date.now(), source: "mobile-panel-active-sync" });
            return;
        }
    }, [activeMobilePanel, shouldUseReviewMobilePanels, shouldUseReviewMobileSheet]);

    useEffect(() => {
        if (!shouldUseReviewMobileSheet) return;
        if (activeMobilePanel !== "document") {
            setActiveMobilePanel("document");
        }
    }, [activeMobilePanel, shouldUseReviewMobileSheet]);

    useEffect(() => {
        if (!shouldUseReviewMobileSheet) return;
        setForcedActiveTab({ key: "contract", ts: Date.now(), source: "mobile-sheet-document-base" });
    }, [currentVersionId, shouldUseReviewMobileSheet]);

    useEffect(() => {
        if (mobileAutoOpenRef.current.analysisKey === reviewMobileAutoOpenAnalysisKey) return;
        mobileAutoOpenRef.current = createReviewMobileAutoOpenState(reviewMobileAutoOpenAnalysisKey);
    }, [reviewMobileAutoOpenAnalysisKey]);

    const scheduleDocumentForceScroll = useCallback((ref, delay = 180) => {
        if (!ref || typeof window === "undefined") return;
        window.setTimeout(() => {
            window.dispatchEvent(new CustomEvent("force-scroll", { detail: ref }));
        }, delay);
    }, []);

    const scrollMobilePanelToTop = useCallback((panel) => {
        if (typeof window === "undefined") return;

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                const selectors = panel === "criteria"
                        ? [
                            ".review-mobile-criteria-drawer .ant-drawer-body",
                            ".review-mobile-criteria-drawer .criteria-list",
                        ]
                        : [
                            ".review-mobile-sheet-content [data-sidebar='comments']",
                            ".review-mobile-sheet-content .review-col.comments-col > div:last-child",
                        ];

                selectors.forEach((selector) => {
                    const el = document.querySelector(selector);
                    if (el && typeof el.scrollTo === "function") {
                        el.scrollTo({ top: 0, behavior: "auto" });
                    } else if (el) {
                        el.scrollTop = 0;
                    }
                });
            });
        });
    }, []);

    const openMobileCriteriaSidebar = useCallback(() => {
        setMobileSheetState("collapsed");
        setMobileSheetHeight(null);
        setIsMobileSheetDragging(false);
        setActiveMobilePanel("document");
        setForcedActiveTab({ key: "contract", ts: Date.now(), source: "mobile-criteria-sidebar" });
        openCriteriaDrawer();
        scrollMobilePanelToTop("criteria");
    }, [openCriteriaDrawer, scrollMobilePanelToTop]);

    const handleMobilePanelChange = useCallback((panel) => {
        if (shouldUseReviewMobileSheet && panel !== "document") {
            if (panel === "criteria") {
                openMobileCriteriaSidebar();
                return;
            }
            setActiveMobileSheetPanel(panel);
            setMobileSheetState("half");
            setMobileSheetHeight(null);
            setActiveMobilePanel("document");
            if (panel === "comments") {
                setRightPanelMode("comments");
                setIsCommentsSidebarOpen(true);
            }
            return;
        }

        setActiveMobilePanel(panel);

        if (panel === "document") {
            setForcedActiveTab({ key: "contract", ts: Date.now(), source: "mobile-panel-nav" });
            return;
        }

        if (panel === "comments") {
            setRightPanelMode("comments");
            setIsCommentsSidebarOpen(true);
            return;
        }

        if (panel === "criteria") {
            closeCriteriaDrawer();
        }
    }, [closeCriteriaDrawer, openMobileCriteriaSidebar, setIsCommentsSidebarOpen, setRightPanelMode, shouldUseReviewMobileSheet]);

    const mobilePanelItems = useMemo(() => {
        const items = [
            {
                key: "document",
                label: t("mobile_panels.document", { defaultValue: "Document" }),
                icon: <FileTextOutlined />,
            },
            {
                key: "comments",
                label: t("mobile_panels.comments", { defaultValue: "Commentaires" }),
                icon: <CommentOutlined />,
                count: normalizedComments.length,
            },
            {
                key: "criteria",
                label: t("mobile_panels.criteria", { defaultValue: "Critères" }),
                icon: <CheckSquareOutlined />,
                count: mobileCriteriaCount,
            },
        ];

        return items;
    }, [mobileCriteriaCount, normalizedComments.length, t]);

    const mobileSheetItems = useMemo(() => ([
        {
            key: "comments",
            label: t("mobile_panels.comments", { defaultValue: "Commentaires" }),
            shortLabel: t("mobile_panels.comments_short", { defaultValue: "Commentaires" }),
            summaryLabel: t("mobile_sheet.comments_summary", { defaultValue: "commentaires" }),
            icon: <CommentOutlined />,
            count: normalizedComments.length,
            loading: !!isAnalyzing,
            loadingLabel: t("mobile_sheet.comments_loading", { defaultValue: "En cours" }),
        },
    ]), [isAnalyzing, normalizedComments.length, t]);

    const markMobileSheetUserDismissed = useCallback((panel = activeMobileSheetPanel) => {
        markReviewMobileAutoOpenDismissed(mobileAutoOpenRef.current, panel);
    }, [activeMobileSheetPanel]);

    const openMobileSheet = useCallback((panel = "comments", state = "half", options = {}) => {
        if (panel === "criteria") {
            openMobileCriteriaSidebar();
            return;
        }
        const { resetScroll = false } = options || {};
        const nextPanel = "comments";
        const shouldResetPanelScroll = resetScroll || nextPanel !== activeMobileSheetPanel || mobileSheetState === "collapsed";
        setActiveMobileSheetPanel(nextPanel);
        setMobileSheetState(state);
        setMobileSheetHeight(null);
        setIsMobileSheetDragging(false);
        setActiveMobilePanel("document");
        setForcedActiveTab({ key: "contract", ts: Date.now(), source: "mobile-sheet-open" });

        if (nextPanel === "comments") {
            setRightPanelMode("comments");
            setIsCommentsSidebarOpen(true);
        }
        if (shouldResetPanelScroll) {
            scrollMobilePanelToTop(nextPanel);
        }
    }, [activeMobileSheetPanel, mobileSheetState, openMobileCriteriaSidebar, scrollMobilePanelToTop, setIsCommentsSidebarOpen, setRightPanelMode]);

    const collapseMobileSheet = useCallback(() => {
        markMobileSheetUserDismissed();
        setMobileSheetState("collapsed");
        setMobileSheetHeight(null);
        setIsMobileSheetDragging(false);
        setActiveMobilePanel("document");
        setForcedActiveTab({ key: "contract", ts: Date.now(), source: "mobile-sheet-collapse" });
    }, [markMobileSheetUserDismissed]);

    const toggleMobileSheetSize = useCallback(() => {
        setMobileSheetHeight(null);
        setMobileSheetState((state) => {
            if (state === "collapsed") return "half";
            if (state === "half") return "full";
            return "half";
        });
    }, []);

    const getMobileSheetHeights = useCallback(() => {
        const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 844;
        const fullHeight = Math.max(320, viewportHeight - 64);
        const halfHeight = Math.min(Math.round(viewportHeight * 0.54), 470, fullHeight);
        const minExpandedHeight = Math.min(Math.max(220, Math.round(viewportHeight * 0.30)), halfHeight);
        return {
            collapsedHeight: 76,
            minExpandedHeight,
            halfHeight,
            fullHeight,
        };
    }, []);

    const handleMobileSheetDragStart = useCallback((event) => {
        if (!shouldUseReviewMobileSheet || mobileSheetState === "collapsed") return;
        const pointerY = event.clientY;
        if (!Number.isFinite(pointerY)) return;

        const heights = getMobileSheetHeights();
        const currentHeight = mobileSheetHeight
            || (mobileSheetState === "full" ? heights.fullHeight : heights.halfHeight);

        mobileSheetDragRef.current = {
            active: true,
            didDrag: false,
            startY: pointerY,
            startHeight: currentHeight,
            currentHeight,
            ...heights,
        };
        setMobileSheetHeight(currentHeight);
        setIsMobileSheetDragging(true);
        event.currentTarget?.setPointerCapture?.(event.pointerId);
        event.preventDefault();
    }, [getMobileSheetHeights, mobileSheetHeight, mobileSheetState, shouldUseReviewMobileSheet]);

    const handleMobileSheetDragMove = useCallback((event) => {
        const drag = mobileSheetDragRef.current;
        if (!drag?.active) return;
        const pointerY = event.clientY;
        if (!Number.isFinite(pointerY)) return;

        const delta = drag.startY - pointerY;
        const nextHeight = Math.max(
            drag.collapsedHeight,
            Math.min(drag.fullHeight, Math.round(drag.startHeight + delta))
        );
        if (Math.abs(nextHeight - drag.startHeight) > 6) {
            drag.didDrag = true;
        }
        drag.currentHeight = nextHeight;
        setMobileSheetHeight(nextHeight);
        event.preventDefault();
    }, []);

    const handleMobileSheetDragEnd = useCallback((event) => {
        const drag = mobileSheetDragRef.current;
        if (!drag?.active) return;
        mobileSheetDragRef.current = { active: false };
        setIsMobileSheetDragging(false);
        event.currentTarget?.releasePointerCapture?.(event.pointerId);

        const finalHeight = drag.currentHeight || drag.startHeight;
        const collapseThreshold = Math.max(160, drag.minExpandedHeight * 0.72);
        const fullThreshold = drag.halfHeight + ((drag.fullHeight - drag.halfHeight) * 0.48);

        if (finalHeight < collapseThreshold) {
            markMobileSheetUserDismissed();
            setMobileSheetHeight(null);
            setMobileSheetState("collapsed");
        } else if (finalHeight >= fullThreshold) {
            setMobileSheetHeight(null);
            setMobileSheetState("full");
        } else {
            setMobileSheetHeight(null);
            setMobileSheetState("half");
        }

        if (drag.didDrag) {
            mobileSheetClickBlockRef.current = true;
            window.setTimeout(() => {
                mobileSheetClickBlockRef.current = false;
            }, 180);
        }
    }, [markMobileSheetUserDismissed]);

    const handleMobileSheetGripClick = useCallback(() => {
        if (mobileSheetClickBlockRef.current) {
            mobileSheetClickBlockRef.current = false;
            return;
        }
        toggleMobileSheetSize();
    }, [toggleMobileSheetSize]);

    const handleMobileSheetGripKeyDown = useCallback((event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        toggleMobileSheetSize();
    }, [toggleMobileSheetSize]);

    const mobileSheetStyle = useMemo(() => {
        if (!mobileSheetHeight || mobileSheetState === "collapsed") return undefined;
        return { "--review-mobile-sheet-current-height": `${Math.round(mobileSheetHeight)}px` };
    }, [mobileSheetHeight, mobileSheetState]);

    useEffect(() => {
        if (!isMobileSheetDragging || typeof window === "undefined") return undefined;

        window.addEventListener("pointermove", handleMobileSheetDragMove, { passive: false });
        window.addEventListener("pointerup", handleMobileSheetDragEnd);
        window.addEventListener("pointercancel", handleMobileSheetDragEnd);

        return () => {
            window.removeEventListener("pointermove", handleMobileSheetDragMove);
            window.removeEventListener("pointerup", handleMobileSheetDragEnd);
            window.removeEventListener("pointercancel", handleMobileSheetDragEnd);
        };
    }, [handleMobileSheetDragEnd, handleMobileSheetDragMove, isMobileSheetDragging]);

    useEffect(() => {
        if (!shouldUseReviewMobileSheet || !isCriteriaDrawerOpen) return;
        scrollMobilePanelToTop("criteria");
    }, [isCriteriaDrawerOpen, scrollMobilePanelToTop, shouldUseReviewMobileSheet]);

    useEffect(() => {
        const actions = computeReviewMobileAutoOpenActions({
            enabled: shouldUseReviewMobileSheet,
            isCreate,
            autoState: mobileAutoOpenRef.current,
            comments: normalizedComments,
        });
        if (actions.length === 0) return;

        actions.forEach((action) => {
            const targetSheetState = mobileSheetState === "collapsed" ? "half" : mobileSheetState;
            if (action.type === "open_comments") {
                mobileAutoOpenRef.current.commentsOpened = true;
                if (action.reason === "first_signal") {
                    mobileAutoOpenRef.current.signalsOpened = true;
                }
                openMobileSheet("comments", targetSheetState, { resetScroll: action.focusSignal });
                if (action.focusSignal) {
                    const target = getReviewMobileCommentTarget(action.comment);
                    setSelectedRef(target.ref);
                    setSelectedCommentId(target.id);
                }
                return;
            }

        });
    }, [
        isCreate,
        mobileSheetState,
        normalizedComments,
        openMobileSheet,
        setSelectedRef,
        shouldUseReviewMobileSheet,
    ]);

    const handleStrategicMemoOpen = useCallback(() => {
        if (!shouldUseReviewMobileSheet) return;
        openMobileSheet("comments", "half", { resetScroll: true });
    }, [openMobileSheet, shouldUseReviewMobileSheet]);

    const referenceReviewCitationHighlights = useMemo(() => {
        const verdicts = Object.values(
            referenceReviewState?.verdicts_by_id || {}
        );
        return verdicts.flatMap((verdict) => (
            Array.isArray(verdict?.contract_evidence)
                ? verdict.contract_evidence
                    .filter((evidence) => evidence?.bbox && evidence?.page)
                    .map((evidence) => ({
                        page: evidence.page,
                        text: evidence.quote,
                        ref: evidence.target_ref || evidence.source_ref,
                        bbox: evidence.bbox,
                        blockId: evidence.target_ref || evidence.source_ref,
                        kind: 'reference_review_evidence',
                    }))
                : []
        ));
    }, [referenceReviewState]);

    const traceEvidenceCitationHighlights = useMemo(() => {
        const highlight = traceEvidenceNavigation?.highlight;
        return highlight ? [highlight] : [];
    }, [traceEvidenceNavigation]);

    const documentCitationHighlights = useMemo(() => (
        [
            ...referenceReviewCitationHighlights,
            ...traceEvidenceCitationHighlights,
        ]
    ), [
        referenceReviewCitationHighlights,
        traceEvidenceCitationHighlights,
    ]);

    // Validation pour l'indicateur visuel dans le formulaire (basé sur le draft)
    const isIncomplete = !isCreate && (
        !metadataDraft?.context ||
        !metadataDraft?.document_type ||
        !metadataDraft?.party_role ||
        !metadataDraft?.studied_party_name ||
        !hasList(metadataDraft?.industry, "industry") ||
        !hasList(metadataDraft?.jurisdiction, "jurisdiction")
    );

    // Validation stricte pour le lancement d'analyse : basé sur les données SAUVEGARDÉES dans le backend
    // Le bouton ne s'active que si les métadonnées sont persistées (après click sur "Enregistrer")
    // Les 5 champs sont obligatoires pour garantir une analyse de qualité :
    // - context : comprendre l'objectif (pré-signature, due diligence, etc.)
    // - document_type : type de contrat (NDA, MSA, SaaS, etc.)
    // - party_role : position contractuelle (acheteur/vendeur) - CRITIQUE pour l'IA
    // - industry : contexte sectoriel
    // - jurisdiction : aspects légaux applicables
    const isMetadataIncompleteForAnalysis = !isCreate && (
        !stage?.main_document?.document_type ||
        !stage?.main_document?.party_role ||
        !stage?.main_document?.studied_party_name ||
        !hasList(stage?.main_document?.industry, "industry") ||
        !hasList(stage?.main_document?.jurisdiction, "jurisdiction") ||
        !stage?.context  // context est stocké dans analyse, pas dans main_document
    );


    const savedMetadataForGate = useMemo(() => ({
        context: stage?.context,
        document_type: stage?.main_document?.document_type,
        party_role: stage?.main_document?.party_role,
        studied_party_name: stage?.main_document?.studied_party_name,
        industry: stage?.main_document?.industry,
        jurisdiction: stage?.main_document?.jurisdiction,
    }), [
        stage?.context,
        stage?.main_document?.document_type,
        stage?.main_document?.industry,
        stage?.main_document?.jurisdiction,
        stage?.main_document?.party_role,
        stage?.main_document?.studied_party_name,
    ]);

    const hasAnyMetadataValue = useCallback((values = {}) => Boolean(
        values?.context ||
        values?.document_type ||
        values?.party_role ||
        hasList(values?.industry, "industry") ||
        hasList(values?.jurisdiction, "jurisdiction")
    ), []);

    const isMetadataDraftComplete = useCallback((values = metadataDraft) => {
        const candidate = hasAnyMetadataValue(values) ? values : savedMetadataForGate;
        return isMetadataComplete(candidate) && hasCompletePov(candidate);
    }, [hasAnyMetadataValue, metadataDraft, savedMetadataForGate]);

    const requestAnalysisMetadata = useCallback((versionId) => {
        if (!isUuid(versionId) || isMetadataDraftComplete()) return;
        setRightPanelMode("comments");
        setIsCommentsSidebarOpen(true);
        setIsConfirmingAnalysisMetadataSkip(false);
        setAnalysisMetadataModalOpen(true);
    }, [isMetadataDraftComplete, setIsCommentsSidebarOpen, setRightPanelMode]);

    const handleSaveAnalysisMetadata = useCallback(async (values) => {
        setIsSavingAnalysisMetadata(true);
        try {
            await saveMetadata(normalizeMetadataDraft(values, { strict: true }));
            setAnalysisMetadataModalOpen(false);
            setForcedActiveTab({ key: "contract", ts: Date.now(), source: "analysis-metadata-save" });
            message.success(t('contract_profile_metadata.saved_launching'));
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                error?.response?.data?.detail ||
                t('contract_profile_metadata.save_error')
            );
        } finally {
            setIsSavingAnalysisMetadata(false);
        }
    }, [saveMetadata, t]);

    const handleSkipAnalysisMetadata = useCallback(() => {
        setIsConfirmingAnalysisMetadataSkip(false);
        setAnalysisMetadataModalOpen(false);
        setRightPanelMode("settings");
        setIsCommentsSidebarOpen(true);
        message.warning(t('contract_profile_metadata.skip_warning'));
    }, [setIsCommentsSidebarOpen, setRightPanelMode, t]);

    // Extraction and party detection precede the existing context form. Saving it
    // only persists metadata; analysis is launched by the explicit analysis button.
    useVersionStatusPolling(
        !isCreate && stage?.id ? documentVersion?.currentVersion : null,
        { onMetadataRequired: requestAnalysisMetadata }
    );

    useEffect(() => {
        if (isCreate) return;
        dispatch(fetchStageData(id_review));
    }, [dispatch, id_review, isCreate]);

    useEffect(() => {
        if (isCreate) return;
        if (commercialControl?.usageSnapshot || commercialControl?.isLoadingUsage) return;
        dispatch(fetchCommercialUsage()).catch(() => {
            // Locks stay conservative if usage cannot be loaded.
        });
    }, [
        commercialControl?.isLoadingUsage,
        commercialControl?.usageSnapshot,
        dispatch,
        isCreate,
    ]);

    useEffect(() => {
        if (isCreate) return;
        if (!currentVersionId) return;
        if (content?.id === currentVersionId) return;
        dispatch(loadVersionContent(currentVersionId));
    }, [content?.id, currentVersionId, dispatch, isCreate]);

    // Handlers
    const handleVersionChange = useCallback((versionId) => {
        dispatch(loadVersionContent(versionId));
    }, [dispatch]);

    const handlePovRequired = useCallback(() => {
        setRightPanelMode("settings");
        setIsCommentsSidebarOpen(true);
        setPovFocusRequest((request) => request + 1);
        if (shouldUseReviewMobileSheet) {
            openMobileSheet("comments", "half", {resetScroll: true});
        } else if (shouldUseReviewMobilePanels) {
            setActiveMobilePanel("comments");
        }
    }, [
        openMobileSheet,
        setIsCommentsSidebarOpen,
        setRightPanelMode,
        shouldUseReviewMobilePanels,
        shouldUseReviewMobileSheet,
    ]);

    const handleCommercialLaunchFailure = useCallback((rawDecisionOrError, launchContext = null) => {
        const decision = normalizeCommercialDecision(rawDecisionOrError);
        const retryContext = buildPartialAnalysisRetryContext(decision, launchContext || {});
        if (retryContext) {
            setPendingPartialLaunch(retryContext);
            dispatch(setPartialAnalysisDecision(decision));
        }
        dispatch(openCommercialGateModal(decision));
        dispatch(fetchCommercialUsage()).catch(() => {
            // Best effort: the modal remains the primary feedback if usage refresh fails.
        });
    }, [dispatch]);

    const launchExistingAnalysisStream = useCallback((criteriaToSend, documentVersionId, launchOptions = {}) => {
        if (!hasCompleteAnalysisPov || isPovTransitioning) {
            handlePovRequired();
            return null;
        }
        const failureContext = {
            criteriaToSend,
            documentVersionId,
            reviewMode: launchOptions.reviewMode,
        };
        const handleThisLaunchFailure = (rawDecisionOrError) => (
            handleCommercialLaunchFailure(rawDecisionOrError, failureContext)
        );
        const result = dispatch(
            launchAnalysisStreamRequest(
                stage.id,
                criteriaToSend,
                documentVersionId,
                {
                    ...launchOptions,
                    onCommercialLaunchFailure: handleThisLaunchFailure,
                }
            )
        );
        if (result?.catch) {
            result.catch(handleThisLaunchFailure);
        }
        return result;
    }, [
        dispatch,
        handleCommercialLaunchFailure,
        handlePovRequired,
        hasCompleteAnalysisPov,
        isPovTransitioning,
        stage.id,
    ]);

    const closeCommercialModal = useCallback(() => {
        dispatch(closeCommercialGateModal());
        dispatch(clearPartialAnalysisDecision());
        setPendingPartialLaunch(null);
    }, [dispatch]);

    const confirmPartialAnalysisLaunch = useCallback(() => {
        if (!pendingPartialLaunch) {
            closeCommercialModal();
            return;
        }

        const {
            criteriaToSend,
            documentVersionId,
            gateCode,
            reviewMode,
        } = pendingPartialLaunch;
        dispatch(closeCommercialGateModal());
        dispatch(clearPartialAnalysisDecision());
        setPendingPartialLaunch(null);
        launchExistingAnalysisStream(criteriaToSend, documentVersionId, {
            ...(reviewMode ? { reviewMode } : {}),
            accepted_partial_analysis: true,
            accepted_gate_code: gateCode,
            accepted_at: new Date().toISOString(),
        });
    }, [closeCommercialModal, dispatch, launchExistingAnalysisStream, pendingPartialLaunch]);

    const handleTemplateChange = useCallback((templateId) => {
        if (!templateId) {
            dispatch(selectTemplate(null));
            dispatch(replaceCriteriaSelection([]));
            return;
        }
        const template = stage?.templates?.find(t => t.id === templateId);
        if (template) {
            dispatch(selectTemplate(template));
            dispatch(replaceCriteriaSelection(
                template.criteria.map(c => ({ id: c.id, ref: [] }))
            ));
        }
    }, [dispatch, stage?.templates]);


    const handleLaunchAnalysis = useCallback(async () => {
        if (!hasCompleteAnalysisPov || isPovTransitioning) {
            handlePovRequired();
            return;
        }
        if (comparisonLaunchPending.current) return;
        const versionPath = comparison.mode === 'document_version' && (stage.selectedCriteria || []).some(
            selection => (stage.criteriaListe || []).some(criterion => criterion.id === selection.id && isReferenceComparisonCriterion(criterion))
        );
        if (!versionPath) {
            setRightPanelMode("comments");
            dispatch(resetAnalysisReasoning());
        }
        if (versionPath && (stage.criteriaListe || []).some(criterion =>
            isReferenceComparisonCriterion(criterion) && criterion.status?.toLowerCase() === 'completed'
            && (stage.selectedCriteria || []).some(selection => selection.id === criterion.id))) {
            setComparisonLaunchError('Une comparaison existe déjà pour cette version et ce point de vue. Consultez les résultats existants.');
            return;
        }

        const globalCriteria = findGlobalAnalysisCriterion(stage.criteriaListe || []);

        const criteriaListe = stage.criteriaListe || [];
        const selectedCriteria = normalizeCriteriaSelectionForAvailableCriteria(
            stage?.selectedCriteria || [],
            criteriaListe
        ).map(({ id, ref }) => ({ id, ref }));
        const selectedIds = selectedCriteria.map(c => c.id);
        const globalId = globalCriteria?.id;
        const isGlobalExecuted = globalCriteria?.status === "completed";

        const nonGlobalSelected = selectedCriteria.filter(c => c.id !== globalId);

        let criteriaToSend = [];

        const isOnlyGlobalSelected =
            selectedIds.length === 1 &&
            selectedIds[0] === globalId &&
            !isGlobalExecuted;

        if ((!selectedIds.length || isOnlyGlobalSelected) && !isGlobalExecuted && globalCriteria) {
            if (!selectedIds.length) {
                dispatch(replaceCriteriaSelection([{id: globalCriteria.id, ref: []}]));
                message.info(t('messages.noCriteriaGlobalAnalysis'));
            }
            criteriaToSend = [{ id: globalCriteria.id, ref: [] }];
        } else if (nonGlobalSelected.length > 0) {
            criteriaToSend = selectedCriteria;
        } else {
            return;
        }

        const criteriaById = new Map(criteriaListe.map((criterion) => [criterion.id, criterion]));
        criteriaToSend = criteriaToSend.map((selection) => {
            const criterion = criteriaById.get(selection?.id);
            if (isReferenceComparisonCriterion(criterion)) {
                return {
                    ...selection,
                    ref: getSelectionReferenceIds(selection).slice(0, 1),
                };
            }
            if (!isReferenceReviewCriterion(criterion)) return selection;
            return {
                ...selection,
                ref: getValidUniqueReferenceIds(selection),
            };
        });

        try {
            criteriaToSend = applyVersionComparisonSelection(criteriaToSend, criteriaListe, comparison);
        } catch (error) {
            setComparisonLaunchError(error.message);
            message.error(error.message);
            return;
        }
        const invalidReferenceSelection = findInvalidReferenceSelection(
            criteriaToSend.filter(selection => selection.reference_type !== 'document_version'),
            criteriaListe
        );
        if (invalidReferenceSelection) {
            const isReferenceReviewIssue = invalidReferenceSelection.code
                === REFERENCE_SELECTION_ERROR_CODES.REFERENCE_REVIEW;
            const isMultipleReferenceIssue = invalidReferenceSelection.reason
                === REFERENCE_SELECTION_ERROR_REASONS.MULTIPLE;
            const translationKey = isReferenceReviewIssue
                ? (isMultipleReferenceIssue
                    ? 'messages.referenceReviewMultipleDocuments'
                    : 'messages.referenceReviewMissingDocument')
                : 'messages.referenceComparisonMissingDocument';
            message.error(t(translationKey));
            return;
        }

        const reviewMode = getComparisonReviewMode(criteriaToSend, criteriaListe);

        const documentVersionId = criteriaToSend[0]?.reference_type === 'document_version'
            ? criteriaToSend[0].examined_document_version_id : [
            documentVersion?.currentVersion,
            documentVersion?.content?.id,
            stage?.versions?.[0]?.id,
        ].find(isUuid);

        if (!isUuid(documentVersionId)) {
            message.error(t('stage.loadError'));
            return;
        }

        if (versionPath) {
            comparisonLaunchPending.current = true;
            setComparisonLaunching(true);
            setComparisonLaunchError('');
            const finishPending = () => {
                comparisonLaunchPending.current = false;
                setComparisonLaunching(false);
            };
            try {
                comparisonCleanup.current = await dispatch(launchVersionComparisonRequest(
                    stage.id, criteriaToSend, documentVersionId, {
                        onRejected: error => {
                            finishPending();
                            const description = versionComparisonError(error);
                            setComparisonLaunchError(description);
                            message.error(description);
                        },
                        onAccepted: () => {
                            finishPending();
                            setRightPanelMode('comments');
                            if (shouldUseReviewMobileSheet) {
                                markReviewMobileLaunchOpenedComments(mobileAutoOpenRef.current);
                                openMobileSheet('comments', 'half', { resetScroll: true });
                            }
                        },
                    }
                ));
            } catch (error) {
                finishPending();
                setComparisonLaunchError(versionComparisonError(error));
            }
            return;
        }

        if (shouldUseReviewMobileSheet) {
            markReviewMobileLaunchOpenedComments(mobileAutoOpenRef.current);
            openMobileSheet("comments", "half", { resetScroll: true });
        }

        const preflightPayload = buildLaunchPreflightPayload({
            stage,
            documentVersion,
            documentVersionId,
            criteriaToSend,
            criteria: criteriaListe,
        });

        await dispatch(runCommercialPreflightLaunchGate(preflightPayload, {
            launch: () => launchExistingAnalysisStream(criteriaToSend, documentVersionId, {
                ...(reviewMode ? { reviewMode } : {}),
            }),
            onBlocked: (decision) => dispatch(openCommercialGateModal(decision)),
            onFailure: (decision) => handleCommercialLaunchFailure(decision),
            onRequiresConfirmation: (decision) => {
                setPendingPartialLaunch({
                    criteriaToSend,
                    documentVersionId,
                    gateCode: decision.code,
                    ...(reviewMode ? { reviewMode } : {}),
                });
                dispatch(setPartialAnalysisDecision(decision));
                dispatch(openCommercialGateModal(decision));
            },
        }));
    }, [
        dispatch,
        documentVersion,
        handleCommercialLaunchFailure,
        comparison,
        handlePovRequired,
        hasCompleteAnalysisPov,
        isPovTransitioning,
        launchExistingAnalysisStream,
        openMobileSheet,
        setRightPanelMode,
        shouldUseReviewMobileSheet,
        stage,
        t,
    ]);

    const handleHover = useCallback((ref) => {
        setHoveredRef(ref);
    }, [setHoveredRef]);

    const handleClick = useCallback((ref, commentId = null, citationNavigation = null) => {
        if (shouldUseReviewMobileSheet) {
            setActiveMobilePanel("document");
            setMobileSheetState("half");
            setMobileSheetHeight(null);
            setIsMobileSheetDragging(false);
        } else if (shouldUseReviewMobilePanels) {
            setActiveMobilePanel("document");
        }
        setForcedActiveTab({ key: "contract", ts: Date.now(), source: "comment-ref" });
        if (citationNavigation && !citationNavigation.unavailable) {
            const requestId = `comment:${commentId}:${++citationNavigationSequence.current}`;
            const navigationRequest = {...citationNavigation, requestId, comment: normalizedComments.find(comment => comment.id === commentId)};
            if (citationNavigation.version.document_version_id === currentVersionId) {
                setComparisonContractNavigationRequest({...navigationRequest, page: citationNavigation.evidence.page});
                setActiveDocumentSource('contract');
            } else {
                setComparisonVersionSource({documentVersionId: citationNavigation.version.document_version_id, title: 'Source contractuelle', isCitation: true});
                setComparisonVersionNavigationRequest(navigationRequest);
                setActiveDocumentSource('reference');
            }
        } else if (!citationNavigation) {
            setComparisonContractNavigationRequest(null);
            setActiveDocumentSource('contract');
        }
        setRightPanelMode("comments");
        setIsCommentsSidebarOpen(true);
        setSelectedRef(ref);
        setSelectedCommentId(commentId);
        if (shouldUseReviewMobilePanels && ref) {
            scheduleDocumentForceScroll(ref, shouldUseReviewMobileSheet ? 120 : 220);
        }

        setTimeout(() => {
            const sidebar = document.querySelector('[data-sidebar="comments"]');
            if (sidebar && ref) {
                const el = commentId
                    ? sidebar.querySelector(`[data-comment-id="${commentId}"]`)
                    : sidebar.querySelector(`[data-ref="${ref}"]`);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
        }, 100);
    }, [currentVersionId, normalizedComments, scheduleDocumentForceScroll, setIsCommentsSidebarOpen, setRightPanelMode, setSelectedRef, shouldUseReviewMobilePanels, shouldUseReviewMobileSheet]);

    const handleReferenceEvidenceClick = useCallback((card, evidence, navigation = {}) => {
        setComparisonVersionSource(null);
        if (!referenceViewerSource || card?.review_id !== referenceViewerSource.reviewId) return;
        if (shouldUseReviewMobileSheet) {
            setActiveMobilePanel('document');
            setMobileSheetState('half');
            setMobileSheetHeight(null);
            setIsMobileSheetDragging(false);
        } else if (shouldUseReviewMobilePanels) {
            setActiveMobilePanel('document');
        }
        setSelectedCommentId(card.id);
        setActiveDocumentSource('reference');
        setReferenceNavigationRequest({
            requestId: `${card.id}:${navigation.index || 0}:${Date.now()}`,
            reviewId: card.review_id,
            requirementId: card.requirement_id,
            evidence,
            index: navigation.index || 0,
            total: navigation.total || 1,
        });
    }, [referenceViewerSource, shouldUseReviewMobilePanels, shouldUseReviewMobileSheet]);

    const handleClauseClick = useCallback((ref) => {
        if (shouldUseReviewMobileSheet) {
            openMobileSheet("comments", "half");
        } else if (shouldUseReviewMobilePanels) {
            setActiveMobilePanel("comments");
        }
        setRightPanelMode("comments");
        setIsCommentsSidebarOpen(true);
        setSelectedRef(ref);
        const directComment = normalizedComments.find(comment => comment.id === ref && canonicalCitations(comment).length);
        const nextCommentId = directComment?.id || getNextCommentIdForRef(
            visibleCardTargetsForSelection || highlightedComments,
            ref,
            selectedCommentId,
        );
        setSelectedCommentId(nextCommentId);
        setCommentFocusRequest((previous) => ({
            commentId: nextCommentId,
            requestId: (previous?.requestId || 0) + 1,
        }));
        const sidebar = document.querySelector('[data-sidebar="comments"]');
        if (sidebar && ref) {
            const el = nextCommentId
                ? sidebar.querySelector(`[data-comment-id="${nextCommentId}"]`)
                : sidebar.querySelector(`[data-ref="${ref}"]`);
            if (el) el.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
    }, [
        highlightedComments,
        normalizedComments,
        openMobileSheet,
        selectedCommentId,
        setIsCommentsSidebarOpen,
        setRightPanelMode,
        setSelectedRef,
        shouldUseReviewMobilePanels,
        shouldUseReviewMobileSheet,
        visibleCardTargetsForSelection,
    ]);

    const handleVersionEvidenceClick = useCallback(({evidence, version, fingerprint}) => {
        if (!evidence || !version?.document_version_id) return;
        const requestId = `${fingerprint || 'comparison'}:${evidence.role}:${evidence.block_id || evidence.page || 'document'}:${Date.now()}`;
        if (evidence.role === 'examined' && evidence.source_kind !== 'citation') {
            const ref = `comparison-version-evidence:${requestId}`;
            setActiveDocumentSource('contract');
            setComparisonContractNavigationRequest({
                requestId,
                evidence,
                page: evidence.page,
                pageNumber: evidence.page,
                ref: evidence.block_id,
                blockId: evidence.block_id,
            });
            setTraceEvidenceNavigation({
                requestId,
                page: evidence.page,
                ref,
                highlight: evidence.bbox ? {
                    page: evidence.page,
                    ref,
                    bbox: evidence.bbox,
                    text: evidence.excerpt || '',
                    kind: 'comparison_version_evidence',
                    blockId: evidence.block_id,
                } : null,
            });
            setSelectedRef(ref);
            return;
        }
        setComparisonVersionSource({
            documentVersionId: version.document_version_id,
            title: [version.version, version.title].filter(Boolean).join(' · '),
            isCitation: evidence.source_kind === 'citation',
        });
        setComparisonVersionNavigationRequest({requestId, evidence});
        setActiveDocumentSource('reference');
    }, []);

    useEffect(() => {
        const handleUnwiredVersionEvidence = (event) => handleVersionEvidenceClick(event.detail || {});
        window.addEventListener('verdictia:comparison-version-evidence-open', handleUnwiredVersionEvidence);
        return () => window.removeEventListener('verdictia:comparison-version-evidence-open', handleUnwiredVersionEvidence);
    }, [handleVersionEvidenceClick]);

    const handleClauseHover = useCallback((ref) => {
        setHoveredRef(ref);
    }, [setHoveredRef]);

    const handleFileSelected = useCallback((file) => {
        if (cinematicActive) return;
        // Démarre l'overlay cinématique immédiatement
        dispatch(startCinematicUpload({ fileName: file?.name }));
        // Puis lance le flux de création (navigation gérée par l'overlay après animation)
        dispatch(RequestCreateAnalyseWithUpload(file, metadataDraft)).catch(() => {
            // Erreur: fermer l'overlay et rester sur la dropzone
            dispatch(resetCinematicUpload());
        });
    }, [cinematicActive, dispatch, metadataDraft]);

    // Handler pour créer un nouveau critère depuis le modal au niveau page
    const handleCriterionSubmit = useCallback(async (values) => {
        try {
            const result = await dispatch(RequestCreateCriterion(values));
            if (result?.id) {
                dispatch(addCriterionToList(result));
                message.success(t('messages.criterionCreated', { defaultValue: 'Critère créé avec succès' }));
                setCriterionModalOpen(false);
            }
        } catch (error) {
            message.error(t('messages.criterionError', { defaultValue: 'Erreur lors de la création' }));
        }
    }, [dispatch, t]);

    return (
        <FeedbackProvider
            analyseId={isUuid(id_review) ? id_review : null}
            documentVersionId={isUuid(currentVersionId) ? currentVersionId : null}
        >
        <div className={`review-wrapper ${shouldUseReviewMobilePanels ? 'review-mobile-shell' : ''} ${shouldUseReviewMobileSheet ? 'review-mobile-sheet-shell' : ''}`}>
            <ReviewHeader
                isCreate={isCreate}
                headerTitle={headerTitle}
                stage={stage}
                criteriaListe={stage?.criteriaListe}
                currentVersion={currentVersion}
                onVersionChange={handleVersionChange}
                onLaunchAnalysis={handleLaunchAnalysis}
                isAnalysisRunning={isAnalyzing}
                isGlobalLoading={isGlobalLoading}
                isProcessing={isProcessing}
                isLoading={isLoading}
                analyseId={id_review}
                isMetadataIncompleteForAnalysis={isMetadataIncompleteForAnalysis}
                hasCompleteAnalysisPov={hasCompleteAnalysisPov}
                isPovTransitioning={isPovTransitioning}
                povTransitionPartyName={povTransitionPartyName}
                onPovRequired={handlePovRequired}
                isCommercialChecking={commercialControl?.isCheckingGate}
                commentsCount={normalizedComments.length}
                commentsLoading={documentVersion?.ia_comments_loading || isPovTransitioning}
            />

            <CommercialGateModal
                open={!!commercialControl?.gateModalOpen}
                decision={commercialControl?.partialAnalysisDecision || commercialControl?.lastDecision}
                currentPlan={commercialControl?.plan || commercialControl?.usageSnapshot?.plan}
                usageSnapshot={commercialControl?.usageSnapshot}
                canManageBilling={canManageBilling}
                billingReturnPath={billingReturnPath}
                onClose={closeCommercialModal}
                onConfirmPartial={confirmPartialAnalysisLaunch}
            />

            {isOcrAnalysisBlocked && (
                <Alert
                    className="ocr-blocked-alert"
                    type="warning"
                    showIcon
                    message={content.ocr_user_message}
                />
            )}

            {shouldShowReviewMobilePanelNav && (
                <nav className="review-mobile-panel-nav" aria-label={t("mobile_panels.label", { defaultValue: "Navigation de revue" })}>
                    {mobilePanelItems.map((item) => (
                        <button
                            key={item.key}
                            type="button"
                            className={`review-mobile-panel-tab ${activeMobilePanel === item.key ? 'is-active' : ''}`}
                            onClick={() => handleMobilePanelChange(item.key)}
                            aria-current={activeMobilePanel === item.key ? "page" : undefined}
                        >
                            <span className="review-mobile-panel-tab__icon">{item.icon}</span>
                            <span className="review-mobile-panel-tab__label">{item.label}</span>
                            {Number(item.count) > 0 && (
                                <span className="review-mobile-panel-tab__count">{item.count}</span>
                            )}
                        </button>
                    ))}
                </nav>
            )}

            {shouldUseReviewMobileSheet && (
                <button
                    type="button"
                    className={`review-mobile-criteria-rail ${isCriteriaDrawerOpen ? 'is-active' : ''}`}
                    onClick={openMobileCriteriaSidebar}
                    aria-label={t("mobile_panels.criteria", { defaultValue: "Critères" })}
                >
                    <span className="review-mobile-criteria-rail__icon">
                        <CheckSquareOutlined />
                    </span>
                    <span className="review-mobile-criteria-rail__label">
                        {t("mobile_panels.criteria", { defaultValue: "Critères" })}
                    </span>
                </button>
            )}

            {/* Drawer pour mobile ET desktop hover */}
            <Drawer
                title={
                    <div className="criteria-drawer-title">
                        <div className="criteria-drawer-title__main">
                            <span className="criteria-drawer-title__icon">
                                <SettingOutlined/>
                            </span>
                            <span>{t('criteria')}</span>
                        </div>
                        <div className="criteria-drawer-title__actions">
                            {/* Épingler dans la sidebar */}
                            {!shouldUseReviewMobileSheet && (
                                <Button
                                    type="text"
                                    icon={<PushpinOutlined/>}
                                    onClick={() => {
                                        closeCriteriaDrawer();
                                        openCriteriaSidebarWithAnimation();
                                    }}
                                    className="criteria-drawer-title__btn"
                                    title={t('criteriaSidebar.pinSidebar')}
                                />
                            )}
                            {/* Fermer */}
                            <Button
                                type="text"
                                icon={<CloseOutlined/>}
                                onClick={closeCriteriaDrawer}
                                className="criteria-drawer-title__btn"
                            />
                        </div>
                    </div>
                }
                placement="left"
                onClose={closeCriteriaDrawer}
                open={isCriteriaDrawerOpen}
                width={shouldUseReviewMobileSheet ? "min(88vw, 380px)" : 480}
                className={`criteria-drawer ${shouldUseReviewMobileSheet ? 'review-mobile-criteria-drawer' : ''}`}
                styles={{body: {padding: '20px 18px 18px'}}}
                closable={false}
                mask={isMobile}
                maskClosable={isMobile}
                zIndex={1000}
                onMouseLeave={() => {
                    if (!isMobile) {
                        closeCriteriaDrawer();
                    }
                }}
                destroyOnClose
            >
                <Criteria
                    comparison={{ ...comparison, launching: comparisonLaunching, error: comparisonLaunchError || comparison.error }}
                    openModalTrigger={openCriteriaModalFlag}
                    animationCycle={criteriaRenderKey}
                    povTransitioning={isPovTransitioning}
                    povTransitionPartyName={povTransitionPartyName}
                />
            </Drawer>

            {/* Structure exacte des 3 colonnes comme dans l'original */}
            <div className={`review-body ${!isCriteriaSidebarOpen ? 'criteria-collapsed' : ''} ${!isCreate && !isCommentsSidebarOpen ? 'comments-collapsed' : ''} ${isCreate ? 'create-mode' : ''} ${shouldUseReviewMobilePanels ? `review-mobile-panel-mode active-mobile-panel-${shouldUseReviewMobileSheet ? 'document' : activeMobilePanel}` : ''} ${shouldUseReviewMobileSheet ? `review-mobile-sheet-mode mobile-sheet-${mobileSheetState} active-mobile-sheet-${activeMobileSheetPanel}` : ''}`}>
                {/* Colonne des critères - visible uniquement hors create */}
                {!isCreate && (
                    (isCriteriaSidebarOpen || (shouldUseReviewMobilePanels && !shouldUseReviewMobileSheet)) ? (
                        // Mode EXPANDED - colonne complète
                        <div className="review-col criteria-col">
                            <div className="column-header">
                                <div className="column-header__left">
                                    <CheckSquareOutlined className="column-header__icon"/>
                                    <span className="column-header__title">{t('stage.criteria')}</span>
                                </div>
                                <div className="column-header__right">
                                    <Button
                                        type="text"
                                        icon={<MenuFoldOutlined/>}
                                        onClick={() => {
                                            if (shouldUseReviewMobilePanels) {
                                                if (shouldUseReviewMobileSheet) {
                                                    collapseMobileSheet();
                                                } else {
                                                    setActiveMobilePanel("document");
                                                }
                                                return;
                                            }
                                            setIsCriteriaSidebarOpen(false);
                                        }}
                                        className="column-header__action"
                                    />
                                </div>
                            </div>
                            <div key={`criteria-expanded-${criteriaRenderKey}`} style={{ animation: 'panelContentIn 0.35s cubic-bezier(0.16,1,0.3,1) both', flex: 1, overflow: 'hidden auto', minHeight: 0 }}>
                                <Criteria
                                    comparison={{ ...comparison, launching: comparisonLaunching, error: comparisonLaunchError || comparison.error }}
                                    key={`criteria-sidebar-${criteriaRenderKey}`}
                                    openModalTrigger={openCriteriaModalFlag}
                                    animationCycle={criteriaRenderKey}
                                    povTransitioning={isPovTransitioning}
                                    povTransitionPartyName={povTransitionPartyName}
                                />
                            </div>
                        </div>
                    ) : (
                        // Mode COLLAPSED - barre verticale avec boutons d'actions
                        <div className="review-col criteria-col collapsed">
                            {/* Bouton ouvrir drawer (survol) */}
                            <Tooltip title={t('criteriaSidebar.viewDrawer')} placement="right">
                                <Button
                                    type="text"
                                    className="criteria-collapsed-btn"
                                    icon={<MenuUnfoldOutlined/>}
                                    onClick={openCriteriaDrawer}
                                />
                            </Tooltip>
                            
                            {/* Bouton épingler en sidebar */}
                            <Tooltip title={t('criteriaSidebar.pinSidebar')} placement="right">
                                <Button
                                    type="text"
                                    className="criteria-collapsed-btn criteria-collapsed-btn--pin"
                                    icon={<PushpinOutlined/>}
                                    onClick={openCriteriaSidebarWithAnimation}
                                />
                            </Tooltip>
                            
                            {/* Bouton ajouter critère - visible si droits */}
                            {canCreateCriteria && (
                                <Tooltip title={t('criteriaSidebar.addCriterion')} placement="right">
                                    <Button
                                        type="text"
                                        className="criteria-collapsed-btn criteria-collapsed-btn--add"
                                        icon={<PlusOutlined/>}
                                        onClick={() => setOpenCriteriaModalFlag(prev => prev + 1)}
                                    />
                                </Tooltip>
                            )}
                            
                            {/* Texte vertical */}
                            <div className="criteria-collapsed-label">
                                <CheckSquareOutlined className="criteria-collapsed-label__icon"/>
                                <span>{t('criteria')}</span>
                            </div>
                        </div>
                    )
                )}

                {/* Colonne document */}
                <div className="review-col document-col">
                    <DocumentPanel
                        isCreate={isCreate}
                        isProcessing={isProcessing}
                        isAnalyzing={isAnalyzing}
                        isGlobalLoading={isGlobalLoading}
                        content={content}
                        currentVersion={currentVersion}
                        hoveredRef={hoveredRef}
                        selectedRef={selectedRef}
                        normalizedComments={highlightedComments}
                        metadataDraft={metadataDraft}
                        forcedActiveTab={forcedActiveTab}
                        onClauseClick={handleClauseClick}
                        onClauseHover={handleClauseHover}
                        onFileSelected={handleFileSelected}
                        citationHighlights={documentCitationHighlights}
                        includeCitationHighlights={
                            shouldUseReviewMobileSheet
                            || referenceReviewCitationHighlights.length > 0
                            || traceEvidenceCitationHighlights.length > 0
                        }
                        contractNavigationRequest={comparisonContractNavigationRequest || traceEvidenceNavigation}
                        activeDocumentSource={activeDocumentSource}
                        onDocumentSourceChange={source => typeof source === 'string' ? setActiveDocumentSource(source) : handleClick(source.ref, source.comment_id, source)}
                        referenceSource={referenceViewerSource}
                        referenceNavigationRequest={referenceNavigationRequest}
                        comparisonVersionSource={comparisonVersionSource}
                        comparisonVersionNavigationRequest={comparisonVersionNavigationRequest}
                    />
                </div>

                {/* Colonne de droite - Commentaires ou Paramètres */}
                {!isCreate && (
                    (isCommentsSidebarOpen || (shouldUseReviewMobilePanels && !shouldUseReviewMobileSheet)) ? (
                        <RightPanel
                            isCreate={isCreate}
                            showSettings={showSettings}
                            normalizedComments={normalizedComments}
                            isIncomplete={isIncomplete}
                            metadataInitialValues={metadataInitialValues}
                            isGlobalLoading={isGlobalLoading}
                            hoveredRef={hoveredRef}
                            selectedRef={selectedRef}
                            selectedCommentId={selectedCommentId}
                            commentFocusRequest={commentFocusRequest}
                            showAllComments={showAllComments}
                            onRightPanelModeChange={setRightPanelMode}
                            onSaveMetadata={saveMetadata}
                            onPovChange={changePov}
                            onMetadataDraftChange={setMetadataDraft}
                            onGuessMetadata={guessMetadata}
                            isGuessingMetadata={isGuessingMetadata}
                            onCommentHover={handleHover}
                            onCommentClick={handleClick}
                            onReferenceEvidenceClick={handleReferenceEvidenceClick}
                            onVersionEvidenceClick={handleVersionEvidenceClick}
                            onToggleShowAll={() => setShowAllComments(v => !v)}
                            onVisibleCommentsChange={handleVisibleCommentsChange}
                            analysisId={id_review}
                            documentVersionId={currentVersionId}
                            partyDetection={documentVersion?.content?.party_detection_json}
                            partyDetectionStatus={documentVersion?.content?.party_detection_status}
                            povTransitioning={isPovTransitioning}
                            povFocusRequest={povFocusRequest}
                            hasCompleteAnalysisPov={hasCompleteAnalysisPov}
                            povResultsError={povResultsError}
                            onRetryPovResults={retryPovResults}
                            onPovRequired={handlePovRequired}
                            onStrategicMemoOpen={handleStrategicMemoOpen}
                            focusStrategicMemoOnShow={shouldUseReviewMobileSheet}
                            onCollapse={() => {
                                if (shouldUseReviewMobilePanels) {
                                    if (shouldUseReviewMobileSheet) {
                                        collapseMobileSheet();
                                    } else {
                                        setActiveMobilePanel("document");
                                    }
                                    return;
                                }
                                setIsCommentsSidebarOpen(false);
                            }}
                        />
                    ) : (
                        <div className="review-col comments-col collapsed">
                            <Tooltip title={t('commentsSidebar.expand', {defaultValue: 'Afficher les commentaires IA'})} placement="left">
                                <Button
                                    type="text"
                                    className="comments-collapsed-btn"
                                    icon={<MenuFoldOutlined/>}
                                    onClick={() => setIsCommentsSidebarOpen(true)}
                                />
                            </Tooltip>

                            <Tooltip
                                title={showSettings
                                    ? t('common.comments', {ns: 'taxonomie'})
                                    : t('common.contract_details', {ns: 'taxonomie'})}
                                placement="left"
                            >
                                <Button
                                    type="text"
                                    className="comments-collapsed-btn comments-collapsed-btn--mode"
                                    icon={showSettings ? <CommentOutlined/> : <FileTextOutlined/>}
                                    onClick={() => {
                                        setRightPanelMode(showSettings ? "comments" : "settings");
                                        setIsCommentsSidebarOpen(true);
                                    }}
                                />
                            </Tooltip>

                            <div className="comments-collapsed-label">
                                {showSettings ? (
                                    <FileTextOutlined className="comments-collapsed-label__icon"/>
                                ) : (
                                    <CommentOutlined className="comments-collapsed-label__icon"/>
                                )}
                                <span>
                                    {showSettings
                                        ? t('common.contract_details', {ns: 'taxonomie'})
                                        : t('common.comments', {ns: 'taxonomie'})}
                                </span>
                            </div>
                        </div>
                    )
                )}
            </div>

            {shouldUseReviewMobileSheet && (
                <section
                    className={`review-mobile-bottom-sheet review-mobile-bottom-sheet--${mobileSheetState} ${isMobileSheetDragging ? 'review-mobile-bottom-sheet--dragging' : ''}`}
                    style={mobileSheetStyle}
                    aria-label={t("mobile_sheet.label", { defaultValue: "Panneau mobile de revue" })}
                >
                    {mobileSheetState === "collapsed" && (
                        <div className="review-mobile-sheet-collapsed">
                            <button
                                type="button"
                                className="review-mobile-sheet-peek"
                                onClick={() => openMobileSheet(activeMobileSheetPanel, "half")}
                                aria-expanded={false}
                            >
                                <span className="review-mobile-sheet-handle" aria-hidden="true" />
                                <span className="review-mobile-sheet-summary">
                                    {mobileSheetItems.map((item) => (
                                        <span key={item.key} className="review-mobile-sheet-summary__item">
                                            <span className="review-mobile-sheet-summary__icon">{item.icon}</span>
                                            <span className="review-mobile-sheet-summary__text">
                                                {Number(item.count) > 0
                                                    ? `${item.count} ${item.summaryLabel || item.shortLabel}`
                                                    : (item.summaryLabel || item.shortLabel)}
                                            </span>
                                            {item.loading && (
                                                <span
                                                    className="review-mobile-sheet-summary__loader"
                                                    aria-label={item.loadingLabel}
                                                />
                                            )}
                                        </span>
                                    ))}
                                </span>
                            </button>
                        </div>
                    )}

                    {mobileSheetState !== "collapsed" && (
                        <div className="review-mobile-sheet-expanded" aria-expanded>
                            <div
                                role="button"
                                tabIndex={0}
                                className="review-mobile-sheet-grip"
                                onClick={handleMobileSheetGripClick}
                                onKeyDown={handleMobileSheetGripKeyDown}
                                onPointerDown={handleMobileSheetDragStart}
                                onPointerMove={handleMobileSheetDragMove}
                                onPointerUp={handleMobileSheetDragEnd}
                                onPointerCancel={handleMobileSheetDragEnd}
                                aria-label={mobileSheetState === "full"
                                    ? t("mobile_sheet.reduce", { defaultValue: "Reduire le panneau" })
                                    : t("mobile_sheet.expand", { defaultValue: "Agrandir le panneau" })}
                            >
                                <span className="review-mobile-sheet-grip__handle" aria-hidden="true" />
                                <span className="review-mobile-sheet-grip__cue" aria-hidden="true" />
                            </div>

                            <div className="review-mobile-sheet-header">
                                <div className="review-mobile-sheet-tabs" role="tablist" aria-label={t("mobile_sheet.tabs", { defaultValue: "Sections mobiles" })}>
                                    {mobileSheetItems.map((item) => (
                                        <button
                                            key={item.key}
                                            type="button"
                                            role="tab"
                                            aria-selected={activeMobileSheetPanel === item.key}
                                            className={`review-mobile-sheet-tab ${activeMobileSheetPanel === item.key ? 'is-active' : ''} ${item.loading ? 'is-loading' : ''}`}
                                            onClick={() => openMobileSheet(item.key, mobileSheetState)}
                                            aria-busy={item.loading ? true : undefined}
                                        >
                                            <span className="review-mobile-sheet-tab__icon">{item.icon}</span>
                                            <span className="review-mobile-sheet-tab__label">{item.shortLabel}</span>
                                            {Number(item.count) > 0 && (
                                                <span className="review-mobile-sheet-tab__count">{item.count}</span>
                                            )}
                                            {item.loading && (
                                                <span className="review-mobile-sheet-tab__loading">
                                                    <span
                                                        className="review-mobile-sheet-tab__spinner"
                                                        aria-hidden="true"
                                                    />
                                                    <span className="review-mobile-sheet-tab__loading-text">
                                                        {item.loadingLabel}
                                                    </span>
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                                <div className="review-mobile-sheet-actions">
                                    <button
                                        type="button"
                                        className="review-mobile-sheet-action"
                                        onClick={toggleMobileSheetSize}
                                        aria-label={mobileSheetState === "full"
                                            ? t("mobile_sheet.half", { defaultValue: "Demi hauteur" })
                                            : t("mobile_sheet.full", { defaultValue: "Plein ecran" })}
                                    >
                                        <MenuUnfoldOutlined />
                                    </button>
                                </div>
                            </div>

                            <div className="review-mobile-sheet-content">
                                <div
                                    key={activeMobileSheetPanel}
                                    className={`review-mobile-sheet-panel-stage is-${activeMobileSheetPanel}`}
                                >
                                    {activeMobileSheetPanel === "comments" && (
                                        <RightPanel
                                            isCreate={isCreate}
                                            showSettings={showSettings}
                                            normalizedComments={normalizedComments}
                                            isIncomplete={isIncomplete}
                                            metadataInitialValues={metadataInitialValues}
                                            isGlobalLoading={isGlobalLoading}
                                            hoveredRef={hoveredRef}
                                            selectedRef={selectedRef}
                                            selectedCommentId={selectedCommentId}
                                            commentFocusRequest={commentFocusRequest}
                                            showAllComments={showAllComments}
                                            onRightPanelModeChange={setRightPanelMode}
                                            onSaveMetadata={saveMetadata}
                                            onPovChange={changePov}
                                            onMetadataDraftChange={setMetadataDraft}
                                            onGuessMetadata={guessMetadata}
                                            isGuessingMetadata={isGuessingMetadata}
                                            onCommentHover={handleHover}
                                            onCommentClick={handleClick}
                                            onReferenceEvidenceClick={handleReferenceEvidenceClick}
                                            onVersionEvidenceClick={handleVersionEvidenceClick}
                                            onToggleShowAll={() => setShowAllComments(v => !v)}
                                            onVisibleCommentsChange={handleVisibleCommentsChange}
                                            analysisId={id_review}
                                            documentVersionId={currentVersionId}
                                            partyDetection={documentVersion?.content?.party_detection_json}
                                            partyDetectionStatus={documentVersion?.content?.party_detection_status}
                                            povTransitioning={isPovTransitioning}
                                            povFocusRequest={povFocusRequest}
                                            hasCompleteAnalysisPov={hasCompleteAnalysisPov}
                                            povResultsError={povResultsError}
                                            onRetryPovResults={retryPovResults}
                                            onPovRequired={handlePovRequired}
                                            onStrategicMemoOpen={handleStrategicMemoOpen}
                                            focusStrategicMemoOnShow={shouldUseReviewMobileSheet}
                                            onCollapse={collapseMobileSheet}
                                        />
                                    )}

                                </div>
                            </div>
                        </div>
                    )}
                </section>
            )}
            
            {/* Modal de création de critère - au niveau page pour être accessible même en collapsed */}
            <CriterionFormModal
                open={criterionModalOpen}
                onClose={() => setCriterionModalOpen(false)}
                onSubmit={handleCriterionSubmit}
                initialValues={null}
                existingSections={existingSections}
            />

            <Modal
                open={analysisMetadataModalOpen}
                centered
                width={700}
                destroyOnClose
                maskClosable={false}
                closable={false}
                keyboard={false}
                className="contract-profile-metadata-modal"
                title={null}
                footer={(
                    <div className="contract-profile-metadata-modal__footer">
                        {isConfirmingAnalysisMetadataSkip ? (
                            <div className="contract-profile-metadata-modal__confirm">
                                <div className="contract-profile-metadata-modal__confirm-message">
                                    <span className="contract-profile-metadata-modal__confirm-icon">
                                        <ExclamationCircleOutlined />
                                    </span>
                                    <div className="contract-profile-metadata-modal__confirm-copy">
                                        <strong>{t('contract_profile_metadata.skip_confirm_title')}</strong>
                                        <span>{t('contract_profile_metadata.skip_confirm_description_1')}</span>
                                        <span>{t('contract_profile_metadata.skip_confirm_description_2')}</span>
                                    </div>
                                </div>
                                <div className="contract-profile-metadata-modal__confirm-actions">
                                    <Button
                                        onClick={() => setIsConfirmingAnalysisMetadataSkip(false)}
                                        disabled={isSavingAnalysisMetadata}
                                    >
                                        {t('contract_profile_metadata.skip_confirm_cancel')}
                                    </Button>
                                    <Button
                                        className="contract-profile-metadata-modal__confirm-launch"
                                        loading={isSavingAnalysisMetadata}
                                        onClick={handleSkipAnalysisMetadata}
                                    >
                                        {t('contract_profile_metadata.skip_confirm_ok')}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <>
                        {!isMetadataDraftComplete() && (
                            <Button
                                className="contract-profile-metadata-modal__skip"
                                disabled={isSavingAnalysisMetadata}
                                onClick={() => setIsConfirmingAnalysisMetadataSkip(true)}
                            >
                                {t('contract_profile_metadata.skip')}
                            </Button>
                        )}
                        <Button
                            type="primary"
                            loading={isSavingAnalysisMetadata}
                            onClick={() => analysisMetadataPanelRef.current?.submitMetadata()}
                        >
                            {t('contract_profile_metadata.save_and_launch')}
                        </Button>
                            </>
                        )}
                    </div>
                )}
            >
                <div className="contract-profile-metadata-modal__hero">
                    <span className="contract-profile-metadata-modal__eyebrow">
                        {t('contract_profile_metadata.eyebrow')}
                    </span>
                    <div className="contract-profile-metadata-modal__hero-main">
                        <div className="contract-profile-metadata-modal__hero-copy">
                            <Title level={3} className="contract-profile-metadata-modal__title">
                                {t('contract_profile_metadata.title')}
                            </Title>
                            <Text className="contract-profile-metadata-modal__subtitle">
                                {t('contract_profile_metadata.subtitle')}
                            </Text>
                        </div>
                        <div className="contract-profile-metadata-modal__hero-action">
                            <Button
                                className="contract-profile-metadata-modal__ai"
                                icon={<ThunderboltOutlined />}
                                loading={isGuessingMetadata}
                                disabled={isSavingAnalysisMetadata || isGuessingMetadata}
                                onClick={() => {
                                    setIsConfirmingAnalysisMetadataSkip(false);
                                    analysisMetadataPanelRef.current?.guessMissingMetadata();
                                }}
                            >
                                {t('contract_profile_metadata.generate_ai')}
                            </Button>
                        </div>
                    </div>
                </div>

                <AnalyseMetadataPanel
                    ref={analysisMetadataPanelRef}
                    initialValues={metadataInitialValues}
                    onChangeDraft={setMetadataDraft}
                    onSave={handleSaveAnalysisMetadata}
                    onGuess={guessMetadata}
                    guessing={isGuessingMetadata}
                    loading={isSavingAnalysisMetadata}
                    disabled={isSavingAnalysisMetadata}
                    showSaveButton={false}
                    showAiGuessButton={false}
                    requireCompleteBeforeSave
                    compact
                    partyDetection={documentVersion?.content?.party_detection_json}
                    partyDetectionStatus={documentVersion?.content?.party_detection_status}
                />
            </Modal>
            <FeedbackDock hidden={isCreate || cinematicActive} />
            <FeedbackDrawer />
        </div>
        </FeedbackProvider>
    );
};

export default StageReview;

